"use client";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import "./AditudeAdUnit.css";
import { queueAditudeSlot } from "./aditudeManager";
import { AditudeGamSize, AditudeSlotTargeting } from "./aditudeTypes";

export interface AditudeAdUnitProps {
  /**
   * The Aditude dashboard slot id (e.g. "pb-slot-content-1"). An id that is
   * not configured in the dashboard fails silently — the div stays empty.
   */
  baseDivId: string;
  /**
   * Position label (e.g. "midcontent", "midcontent2"). Used as a CSS hook, and
   * sent to the wrapper as the slot's `pos` targeting when `sendPosTargeting`
   * is set — the wrapper keys both GAM targeting and the slot's creative sizes
   * off `pos`.
   */
  positionLabel?: string;
  /**
   * Send `pos: positionLabel` as slot targeting. Off by default so existing
   * placements keep the exact wrapper behaviour they shipped with.
   */
  sendPosTargeting?: boolean;
  /** Extra slot-level targeting merged after `pos`. */
  slotTargeting?: AditudeSlotTargeting;
  /**
   * Explicit GAM sizes for this slot. Leave unset to let the wrapper choose
   * from its own per-position, per-device size table.
   */
  gamSizes?: AditudeGamSize[];
  /** Editor-facing label, surfaced as a data attribute for debugging. */
  title?: string;
  /**
   * Reserved height in px so an empty/slow slot doesn't collapse or shift
   * layout (CLS). The wrapper resizes the creative inside it.
   */
  minHeight?: number;
  /** Render a small caption with the generated ids (POC/QA aid). */
  showDebug?: boolean;
  className?: string;
  /**
   * When true, hide the whole placement if the auction returns nothing, instead
   * of leaving a reserved gap. Height is still reserved while the auction runs,
   * so a filled slot never shifts the layout. Defaults to false — every existing
   * caller keeps its reserved-gap behaviour.
   */
  collapseIfEmpty?: boolean;
  /**
   * How long to wait for a creative before treating the slot as unfilled.
   * Aditude runs a header-bidding auction first, so this needs to outlast it.
   */
  emptyCheckDelayMs?: number;
  /**
   * Hand the slot to the wrapper only once it comes within `lazyLoadMarginPx`
   * of the viewport, so below-the-fold placements don't auction on page load.
   * Set false for above-the-fold slots that should request immediately.
   */
  lazyLoad?: boolean;
  /** How far outside the viewport a lazy slot is mapped (and its empty check starts). */
  lazyLoadMarginPx?: number;
}

/** How often the slot is measured for a creative. */
const POLL_MS = 500;

/** Multiples of `emptyCheckDelayMs` to keep watching before giving up. */
const EMPTY_CHECK_WINDOWS = 7;

/**
 * An ad iframe at or below this height is a blank/tracking creative (GAM serves
 * a 9×1 one when nothing wins), not something a reader can see.
 */
const MIN_CREATIVE_PX = 1;

/** Default distance from the viewport at which a lazy slot is mapped. */
const DEFAULT_LAZY_LOAD_MARGIN_PX = 600;

/**
 * One Aditude-managed ad placement. Renders an EMPTY div with a unique,
 * hydration-safe id and maps it to the Aditude slot once it nears the viewport
 * (or at mount with `lazyLoad={false}`). The same
 * template rendered twice on a page gets two distinct divIds — the wrapper
 * indexes the GAM units `_0`, `_1`, … Re-mount (SPA navigation) generates a
 * fresh divId and a fresh auction; there is no teardown API, unmounted divs
 * simply go inert.
 *
 * DOM structure mirrors the ETG reference implementation.
 */
const AditudeAdUnit = ({
  baseDivId,
  positionLabel = "content",
  sendPosTargeting = false,
  slotTargeting,
  gamSizes,
  title,
  minHeight = 90,
  showDebug = false,
  className = "",
  collapseIfEmpty = false,
  emptyCheckDelayMs = 3000,
  lazyLoad = true,
  lazyLoadMarginPx = DEFAULT_LAZY_LOAD_MARGIN_PX,
}: AditudeAdUnitProps) => {
  const reactId = useId();
  const divId = useMemo(
    () => `ad-${baseDivId}-${reactId.replace(/[^a-zA-Z0-9]/g, "")}`,
    [baseDivId, reactId],
  );
  const adUnitRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const [isNearViewport, setIsNearViewport] = useState(false);

  // Callers build these inline (e.g. buildSlotTargeting(slot.key_values)), so a
  // new object each render would re-map the slot and re-run the auction on any
  // unrelated parent re-render. Key the effect on the value, not the identity.
  const targetingKey = JSON.stringify(slotTargeting || {});
  const gamSizesKey = JSON.stringify(gamSizes || []);

  const shouldMap = !lazyLoad || isNearViewport;

  useEffect(() => {
    if (!baseDivId || !shouldMap) return undefined;
    // Deferred one macrotask: React runs child effects before parent effects,
    // so mapping synchronously here would enter tude.cmd BEFORE the page's
    // setPageTargeting/setAdUnitPath — the wrapper contract needs page context
    // first. The timeout lands after the whole mount's effect flush.
    const targeting: AditudeSlotTargeting = {
      ...(sendPosTargeting && positionLabel ? { pos: positionLabel } : {}),
      ...(slotTargeting || {}),
    };
    const timer = setTimeout(() => {
      queueAditudeSlot({
        baseDivId,
        divId,
        ...(Object.keys(targeting).length ? { targeting } : {}),
        ...(gamSizes?.length ? { gamSizes } : {}),
      });
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDivId, divId, sendPosTargeting, positionLabel, targetingKey, gamSizesKey, shouldMap]);

  // Drives both lazy mapping (above) and the empty-check countdown (below): a
  // deep placement is neither auctioned nor judged empty before the reader
  // approaches it. Without an IntersectionObserver (jsdom, old browsers) the
  // slot counts as near at mount, which is the pre-existing behaviour.
  useEffect(() => {
    const el = adUnitRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setIsNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: `${lazyLoadMarginPx}px` },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [divId, lazyLoadMarginPx]);

  // GAM signals "no creative" by leaving the div childless, by injecting a
  // zero-height iframe, or by serving a 1px-tall blank one inside a wrapper div
  // that still has height from the inline iframe's line box. So where a child
  // holds iframes, judge it by the iframes and by any non-iframe content beside
  // them — the wrapper's own height proves nothing.
  const isVisibleCreative = (child: HTMLElement) => {
    const frames =
      child.tagName === "IFRAME" ? [child] : Array.from(child.querySelectorAll("iframe"));
    if (!frames.length) return child.offsetHeight > 0;
    if (frames.some((frame) => frame.offsetHeight > MIN_CREATIVE_PX)) return true;
    return Array.from(child.querySelectorAll<HTMLElement>("*")).some(
      (el) =>
        el.tagName !== "IFRAME" &&
        !el.closest("iframe") &&
        // A wrapper around an iframe only has height from the iframe's line box.
        !el.querySelector("iframe") &&
        el.offsetHeight > 0,
    );
  };

  const hasCreative = () => {
    const el = adUnitRef.current;
    if (!el) return false;
    return Array.from(el.children).some((child) => isVisibleCreative(child as HTMLElement));
  };

  // Watch the slot until a creative lands or the auction window closes. Polling
  // (rather than a MutationObserver) also catches an iframe that is resized to
  // its creative height without a childList/attribute mutation on our node.
  // Re-armed on divId change because an SPA re-mount runs a brand-new auction.
  useEffect(() => {
    if (!baseDivId || !isNearViewport) return undefined;
    setIsFilled(false);
    setIsEmpty(false);
    // Header bidding can fill LATE, so the window outlasts a single auction.
    const maxPolls = Math.ceil((Math.max(emptyCheckDelayMs, 1) * EMPTY_CHECK_WINDOWS) / POLL_MS);
    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;

    const poll = () => {
      polls += 1;
      if (hasCreative()) {
        setIsFilled(true);
        return;
      }
      if (polls >= maxPolls) {
        if (collapseIfEmpty) setIsEmpty(true);
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    timer = setTimeout(poll, POLL_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDivId, divId, collapseIfEmpty, emptyCheckDelayMs, isNearViewport]);

  if (!baseDivId) return null;

  // Height is reserved for the whole auction window so the slot never shifts
  // layout, then released — to the creative's own height once it lands, or to
  // nothing once the slot is confirmed empty.
  const reservedHeight = isFilled || isEmpty ? 0 : minHeight;
  // Collapse with height:0/overflow:hidden rather than display:none so a
  // late-arriving creative stays measurable (a display:none subtree reports
  // every child as 0-height) and can be un-collapsed by the re-check above.
  const collapsedStyle = { height: 0, overflow: "hidden" as const };

  return (
    <aside
      className={`advertisement advertisement--${positionLabel} aditude position-${positionLabel} ${className}`.trim()}
      data-slot-title={title}
      data-empty={isEmpty || undefined}
      style={isEmpty ? collapsedStyle : undefined}
      data-testid='aditude-ad'>
      <div
        className='advertisement__container'
        data-position={positionLabel}
        style={{ minHeight: reservedHeight }}>
        <div
          id={divId}
          ref={adUnitRef}
          className='advertisement__ad-unit'
          style={{ minHeight: reservedHeight }}
        />
      </div>

      {showDebug && (
        <div
          className='advertisement__debug'
          style={{ font: "11px monospace", color: "#889", textAlign: "center", padding: 4 }}>
          {title || positionLabel} · baseDivId=<code>{baseDivId}</code> · divId=<code>{divId}</code>
        </div>
      )}
    </aside>
  );
};

export default AditudeAdUnit;
